import {ComponentRef, useCallback, useReducer, useRef} from 'react';
import {useClickOutsideOf} from 'hooks';
import {useARStore} from 'stores';
import {classNames} from 'utils';

interface IARButtonScreenshotPreview {}

const ARButtonScreenshotPreview = ({}: IARButtonScreenshotPreview) => {
  const [enlarged, toggleEnlarged] = useReducer(s => !s, false);
  const screenshotData = useARStore(s => s.screenshotData);
  const imageRef = useRef<ComponentRef<'img'>>(null);
  const isModelInteractiveRef = useARStore(s => s.isModelInteractiveRef);

  const generateFilename = () => {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const date = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`;
    const time = `${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
    return `${date}-${time}_dasar.jpg`;
  };

  const handleShare = async () => {
    const response = await fetch(screenshotData);
    const blob = await response.blob();
    const file = new File([blob], generateFilename(), {type: blob.type});
    if (navigator.canShare && navigator.canShare({files: [file]})) {
      await navigator.share({files: [file], url: location.href});
    } else {
      alert('Sharing not supported on this browser or device.');
    }
  };

  const handleDownload = () => {
    const a = document.createElement('a');
    a.href = screenshotData;
    a.download = generateFilename();
    a.click();
  };

  const handleClickImage = () => {
    if (!enlarged) {
      toggleEnlarged();
    }
  };

  useClickOutsideOf(
    imageRef,
    useCallback(() => {
      toggleEnlarged();
    }, [toggleEnlarged]),
    enlarged,
  );

  if (!screenshotData) {
    return null;
  }

  return (
    <div
      className={classNames(
        'absolute z-20 border-white aspect-[0.6] overflow-hidden pointer-events-auto transition-all duration-300',
        enlarged
          ? 'w-2/3 bottom-36 left-[50%] transform -translate-x-[50%] border-2 rounded-xl shadow-lg'
          : 'w-20 bottom-6 left-4 border rounded-lg shadow',
      )}>
      <img
        ref={imageRef}
        src={screenshotData}
        alt="screenshot_preview"
        className="w-full h-full"
        onTouchStart={() => (isModelInteractiveRef.current = false)}
        onTouchEnd={() => (isModelInteractiveRef.current = true)}
        onClick={handleClickImage}
      />
      {enlarged && (
        <div className="absolute bottom-0 left-0 right-0 text-white font-medium bg-white flex">
          <button
            type="button"
            className="bg-black w-full p-2 border-white active:bg-white active:text-black border-t-2 border-r-2 "
            onClick={handleShare}>
            Share
          </button>
          <button
            type="button"
            className="bg-black w-full px-2 py-4 border-white active:bg-white active:text-black border-t-2"
            onClick={handleDownload}>
            Download
          </button>
        </div>
      )}
    </div>
  );
};

export default ARButtonScreenshotPreview;
